import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { HRPromoCard } from '../../components/HRPromoBanner';
import { getLatestReleases } from '../../data/pressReleases';
import {
  ArrowRight, ChevronDown, CheckCircle2,
  FileText, Shield, Zap, Building2, BarChart3,
  HelpCircle, Receipt, Scale, Plug, Clock,
  Hotel, Headphones, AlertTriangle, XCircle, Loader, Lock,
  Globe, Wifi, Home as HomeIcon, Laptop, Settings, Users,
  ShieldCheck, Cloud, BadgeCheck, Coffee, Gift, Sparkles, Ticket, Calendar
} from 'lucide-react';
import beds24Logo from '../../assets/images/partners/beds24.jpeg';
import siteminderLogo from '../../assets/images/partners/siteminder.jpeg';
import littleHotelierLogo from '../../assets/images/partners/little-hotelier.jpeg';
import guestyLogo from '../../assets/images/partners/guesty.jpeg';
import lhdnLogo from '../../assets/images/trust/lhdn-myinvois.jpeg';

/* ─── scroll-reveal ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('revealed'); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-el ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function SectionTag({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-[#2563EB] text-sm font-medium mb-4">
      {children}
    </span>
  );
}

const heading = { fontFamily: "'Plus Jakarta Sans', 'Manrope', sans-serif" };

/* ─── FAQ Section ─── */
function FaqSection() {
  const [openFaqs, setOpenFaqs] = useState([1]);
  const toggleFaq = (id) => {
    setOpenFaqs(prev => prev.includes(id) ? prev.filter(faqId => faqId !== id) : [...prev, id]);
  };

  const faqData = [
    { id: 1, question: 'What is StayMii?', answer: 'StayMii is a Malaysia e-invoice plugin that integrates with your existing PMS, channel manager, or business system to automate LHDN-compliant e-invoice generation, tax reporting, and audit-ready records. No system change required.' },
    { id: 2, question: 'Do I need to change my PMS to use StayMii?', answer: "No. StayMii is designed to work alongside your existing PMS or channel manager. It connects to your current system, pulls booking and payment data automatically, and handles e-invoicing in the background. There's no migration, no retraining, and no disruption to your daily operations." },
    { id: 3, question: 'How does Malaysia e-invoice work for hotels?', answer: "Malaysia's e-Invoice system (MyInvois by LHDN) requires businesses to generate, validate, and submit invoices digitally. StayMii automates this entire workflow: it pulls data from your PMS or channel manager, converts it into LHDN-compliant format, submits directly to MyInvois, and tracks every invoice in real time." },
    { id: 4, question: 'Which PMS and channel managers does StayMii integrate with?', answer: 'StayMii currently integrates with Beds24, with Little Hotelier, Mews, and Guesty integrations coming soon. Our integration layer is designed to connect with any PMS or channel manager that provides API access. Contact us if your system is not listed.' },
    { id: 5, question: "What if I don't have a PMS?", answer: "StayMii Lite is an optional lightweight operations tool for small operators without an existing PMS. It helps manage bookings, guest records, and payments — fully integrated with StayMii's e-invoicing system. It's ideal for homestays, small hotels, and Airbnb hosts who currently manage on spreadsheets." },
    { id: 6, question: 'Is StayMii suitable for Airbnb hosts and short-term rental operators?', answer: 'Absolutely. StayMii is built for all types of accommodation — boutique hotels, service apartments, homestays, and Airbnb operators. We handle multi-property invoicing, OTA booking sync, and LHDN compliance automation. Plans start from RM59/month.' },
    { id: 7, question: 'How quickly can I go live?', answer: "Most hotels and operators go live within a few days. Since StayMii plugs into your existing PMS or channel manager, there's no complex migration. Our team assists with setup and integration to ensure a smooth launch." },
  ];

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="faq-section">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50/50 to-white pointer-events-none" />
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-blue-100/30 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-indigo-100/20 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2">
            <Reveal>
              <div className="lg:sticky lg:top-32">
                <SectionTag><HelpCircle className="h-4 w-4" /> FAQ</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight" style={heading}>
                  Frequently Asked<br />Questions
                </h2>
                <p className="text-slate-500 text-base leading-relaxed mb-8">
                  Everything you need to know about e-invoice Malaysia, PMS integration, and LHDN compliance.
                </p>
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <p className="text-sm font-semibold text-slate-900 mb-2">Still have questions?</p>
                  <p className="text-sm text-slate-500 mb-4">Our team is happy to help you get started.</p>
                  <a href="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors">
                    Contact our team <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-3">
            <Reveal delay={100}>
              <div className="space-y-3">
                {faqData.map((faq, idx) => {
                  const isOpen = openFaqs.includes(faq.id);
                  return (
                    <div
                      key={faq.id}
                      className={`rounded-2xl overflow-hidden transition-all duration-300 ${
                        isOpen
                          ? 'bg-white border border-slate-200 shadow-lg shadow-slate-200/50'
                          : 'bg-white/60 border border-slate-100 hover:bg-white hover:border-slate-200'
                      }`}
                      data-testid={`faq-item-${faq.id}`}
                    >
                      <button
                        onClick={() => toggleFaq(faq.id)}
                        className="w-full flex items-center gap-4 p-6 text-left transition-colors"
                        data-testid={`faq-toggle-${faq.id}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${
                          isOpen ? 'bg-[#2563EB]' : 'bg-slate-100'
                        }`}>
                          <span className={`text-xs font-bold transition-colors duration-300 ${isOpen ? 'text-white' : 'text-slate-400'}`}>
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                        </div>
                        <span className={`flex-1 font-semibold text-base transition-colors duration-300 ${isOpen ? 'text-slate-900' : 'text-slate-700'}`}>
                          {faq.question}
                        </span>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                          isOpen ? 'bg-blue-50 rotate-180' : 'bg-slate-50'
                        }`}>
                          <ChevronDown className={`w-4 h-4 transition-colors duration-300 ${isOpen ? 'text-[#2563EB]' : 'text-slate-400'}`} />
                        </div>
                      </button>
                      <div style={{ maxHeight: isOpen ? '500px' : '0px', opacity: isOpen ? 1 : 0, overflow: 'hidden', transition: 'all 0.4s ease' }}>
                        <div className="px-6 pb-6 pl-[72px]">
                          <p className="text-sm text-slate-500 leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════ HOMEPAGE ═══════════════════════ */
const Home = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const faqSchemaData = [
    { q: 'What is StayMii?', a: 'StayMii is a Malaysia e-invoice plugin that integrates with your existing PMS or channel manager to automate LHDN-compliant e-invoice generation and tax reporting.' },
    { q: 'Do I need to change my PMS to use StayMii?', a: 'No. StayMii works alongside your existing PMS such as Beds24 and Guesty. No migration required.' },
    { q: 'How does Malaysia e-invoice work for hotels?', a: "Malaysia's MyInvois system requires digital invoice submission to LHDN. StayMii automates the entire process from data capture to submission." },
    { q: 'Is StayMii suitable for Airbnb hosts?', a: 'Yes. StayMii handles e-invoicing for all accommodation types including Airbnb, short-term rentals, boutique hotels, and homestays.' },
  ];

  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqSchemaData.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.textContent = JSON.stringify(faqSchema);
    const existing = document.getElementById('faq-schema');
    if (existing) existing.remove();
    document.head.appendChild(script);
    return () => { const el = document.getElementById('faq-schema'); if (el) el.remove(); };
  }, []);

  const partners = [
    { name: 'Beds24', logo: beds24Logo, url: 'https://www.beds24.com', tag: 'Officially Live – 23 October 2026' },
    { name: 'SiteMinder', logo: siteminderLogo, url: 'https://www.siteminder.com', tag: 'Application In Progress' },
    { name: 'Little Hotelier', logo: littleHotelierLogo, url: 'https://www.littlehotelier.com' },
    { name: 'Guesty', logo: guestyLogo, url: 'https://www.guesty.com' },
    { name: 'Mews', logo: null, url: 'https://www.mews.com', tag: 'Integration Coming Soon' },
  ];

  return (
    <>
      <SEO
        title="StayMii | Malaysia Hotel PMS, E-Invoice & Free HR System"
        description="Automate hotel operations, bookings, invoicing, LHDN e-invoice compliance, and HR recruitment with StayMii Malaysia. Includes a free HR application form & applicant tracking system for SMEs."
        keywords="e invoice malaysia, lhdn e invoice, myinvois api, hotel pms malaysia, malaysia e-invoice software, hotel e invoice system, pms e invoice integration, channel manager e invoice, hotel management system malaysia, homestay management software malaysia, free hr system malaysia, free job application form, hr application form, applicant tracking system malaysia"
        canonicalUrl="/"
      />
      <style>{`
        .reveal-el { opacity:0; transform:translateY(24px); transition: opacity .6s cubic-bezier(.16,1,.3,1), transform .6s cubic-bezier(.16,1,.3,1); }
        .reveal-el.revealed { opacity:1; transform:translateY(0); }
        @keyframes flowPulse { 0%,100% { opacity:.4; } 50% { opacity:1; } }
        @keyframes dotTravel { 0% { left:0; opacity:0; } 10% { opacity:1; } 90% { opacity:1; } 100% { left:calc(100% - 8px); opacity:0; } }
        @keyframes dashGlow { 0%,100% { box-shadow: 0 0 0 0 rgba(37,99,235,0); } 50% { box-shadow: 0 0 30px 8px rgba(37,99,235,0.08); } }
        @keyframes statusBlink { 0%,100% { opacity:1; } 50% { opacity:.5; } }
        .step-card { animation: dashGlow 4s ease-in-out infinite; }
        .step-card:nth-child(2) { animation-delay: 1.3s; }
        .step-card:nth-child(3) { animation-delay: 2.6s; }
        .travel-dot { position:absolute; width:8px; height:8px; border-radius:50%; top:50%; margin-top:-4px; animation: dotTravel 2.5s ease-in-out infinite; }
        .travel-dot-2 { animation-delay: 0.8s; }
      `}</style>
      <div className="min-h-screen bg-white" data-testid="homepage">
        <LandingNavBar />

        {/* ═══════ ANNOUNCEMENT BANNER — Beds24 e-Invoice official launch ═══════ */}
        <Link
          to="/press/beds24-e-invoice-official-launch"
          data-testid="beds24-launch-banner"
          className="block bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center text-sm font-medium py-2.5 px-4 hover:from-blue-500 hover:to-indigo-500 transition-colors"
        >
          <span className="inline-flex items-center gap-2 flex-wrap justify-center">
            <span className="px-2 py-0.5 rounded-full bg-white/20 text-[11px] font-semibold uppercase tracking-wide">Now Live</span>
            StayMii &times; Beds24 e-Invoice — officially live 23 October 2026.
            <span className="underline underline-offset-2 font-semibold">Get started →</span>
          </span>
        </Link>


        {/* ═══════ 1. HERO — PRIMARY H1 ═══════ */}
        <section className="relative pt-28 pb-20 lg:pt-32 lg:pb-28 bg-[#0B1120] overflow-hidden" data-testid="hero-section">
          <div className="absolute top-0 left-0 w-[700px] h-[700px] bg-blue-600/6 rounded-full blur-[140px] pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none" aria-hidden="true" style={{contain: 'layout paint'}} />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <Reveal>
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-xs font-medium mb-8">
                    <Shield className="w-3.5 h-3.5" /> #1 e-Invoice Plugin for Malaysian Hospitality
                  </div>
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6" data-testid="hero-headline">
                    Malaysia e-Invoice Plugin for PMS & Channel Manager Integration
                  </h1>
                  <p className="text-white/65 text-base lg:text-lg leading-relaxed mb-8 max-w-xl" data-testid="hero-subheadline">
                    StayMii automates LHDN-compliant <strong className="text-white/90">e-invoicing</strong> for hotels, Airbnb hosts, and short-term rental operators. Integrates with your existing <Link to="/pms-e-invoice-integration" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">PMS</Link> or <Link to="/channel-manager-e-invoice" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">channel manager</Link> — no system change required.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 mb-8">
                    <Link to="/einvoice/portal/auth" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold text-sm hover:shadow-2xl hover:shadow-blue-500/20 transition-all" data-testid="hero-cta-primary">
                      Get Started Free <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                    <Link to="/free-invoice-generator" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm hover:shadow-2xl hover:shadow-emerald-500/20 transition-all" data-testid="hero-cta-free-tool">
                      Free Invoice Generator <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                    <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/5 text-white border border-white/15 hover:bg-white/10 rounded-xl font-semibold text-sm transition-all" data-testid="hero-cta-secondary">
                      <Headphones className="w-4 h-4" /> Book a Consultation
                    </Link>
                  </div>
                  <div className="flex flex-wrap gap-4 text-white/40 text-xs">
                    <span><CheckCircle2 className="inline h-3 w-3 mr-1 text-emerald-400/60" />LHDN MyInvois Compliant</span>
                    <span><CheckCircle2 className="inline h-3 w-3 mr-1 text-emerald-400/60" />Works with Any PMS</span>
                    <span><CheckCircle2 className="inline h-3 w-3 mr-1 text-emerald-400/60" />From RM59/month</span>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={150}>
                <div className="relative">
                  <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                    <img
                      srcSet="/hero-dashboard.webp 800w, /hero-dashboard-lg.webp 1200w"
                      sizes="(max-width: 768px) 100vw, 50vw"
                      src="/hero-dashboard-lg.webp"
                      alt="StayMii e-invoice dashboard — LHDN MyInvois submission, PMS integration, and revenue analytics for Malaysian hotels"
                      className="w-full h-auto"
                      loading="eager"
                      fetchpriority="high"
                      width={1200}
                      height={800}
                      decoding="async"
                    />
                  </div>
                  <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg border border-slate-100 px-4 py-3 hidden lg:flex items-center gap-3">
                    <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900">LHDN Compliant</p>
                      <p className="text-[10px] text-slate-500">MyInvois API Ready</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ═══════ 1a. STAYMII × STARBUCKS PARTNERSHIP — HIDDEN pending Starbucks design approval ═══════ */}
        {false && (
        <section
          className="relative overflow-hidden py-20 lg:py-24"
          style={{ background: 'linear-gradient(135deg, #0A3D2C 0%, #0F5138 45%, #00704A 100%)' }}
          data-testid="starbucks-partnership-section"
        >
          {/* soft glows + subtle grain */}
          <div className="absolute -top-24 -left-16 w-[420px] h-[420px] bg-emerald-400/15 rounded-full blur-[130px] pointer-events-none" aria-hidden="true" />
          <div className="absolute -bottom-24 right-0 w-[380px] h-[380px] bg-[#00704A]/40 rounded-full blur-[120px] pointer-events-none" aria-hidden="true" />
          <div
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            aria-hidden="true"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
          />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* ── LEFT: value prop + CTA ── */}
              <Reveal>
                <div>
                  <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/90 text-[11px] font-bold uppercase tracking-[0.16em] mb-6 backdrop-blur-sm" data-testid="starbucks-badge">
                    <Sparkles className="h-3.5 w-3.5 text-amber-300" /> New Partner Perk
                  </span>

                  <h2 className="text-3xl sm:text-4xl lg:text-[46px] font-extrabold text-white leading-[1.08] mb-5 tracking-tight" style={heading}>
                    Join StayMii &amp; unlock{' '}
                    <span className="text-amber-300">15% off Starbucks</span>{' '}
                    for your guests.
                  </h2>

                  <p className="text-white/75 text-base lg:text-lg leading-relaxed mb-8 max-w-xl">
                    Sign up as a StayMii partner and give every guest a reason to smile — exclusive{' '}
                    <strong className="text-white">15% off at participating Starbucks stores in Malaysia</strong>.
                    Just present a StayMii booking confirmation or e-Ticket to redeem.
                  </p>

                  {/* perk pills */}
                  <div className="grid sm:grid-cols-2 gap-3 mb-9">
                    {[
                      { icon: Coffee, text: '15% off drinks & food' },
                      { icon: Ticket, text: 'Redeem with booking or e-Ticket' },
                      { icon: Gift, text: 'More value for your guests' },
                      { icon: Building2, text: 'For hotels, museums & attractions' },
                    ].map((p, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3 backdrop-blur-sm" data-testid={`starbucks-perk-${i}`}>
                        <span className="w-8 h-8 rounded-lg bg-[#00704A] ring-1 ring-white/20 flex items-center justify-center shrink-0">
                          <p.icon className="w-4 h-4 text-white" strokeWidth={2.2} />
                        </span>
                        <span className="text-sm font-semibold text-white/90 leading-tight">{p.text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/einvoice/portal/auth"
                      className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-amber-300 hover:bg-amber-200 text-[#0A3D2C] rounded-xl font-bold text-sm shadow-2xl shadow-black/20 hover:shadow-amber-300/30 transition-all"
                      data-testid="starbucks-cta-signup"
                    >
                      Join StayMii &amp; Get the Perk <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/10 text-white border border-white/25 hover:bg-white/15 rounded-xl font-semibold text-sm transition-all backdrop-blur-sm"
                      data-testid="starbucks-cta-partner"
                    >
                      <Headphones className="w-4 h-4" /> Become a Partner
                    </Link>
                  </div>

                  <p className="text-white/45 text-[11px] mt-5">*Terms &amp; conditions apply. Offer available at participating Starbucks outlets in Malaysia.</p>
                </div>
              </Reveal>

              {/* ── RIGHT: poster visual ── */}
              <Reveal delay={150}>
                <div className="relative">
                  <div className="absolute -inset-3 bg-white/5 rounded-[28px] blur-xl" aria-hidden="true" />
                  <div className="relative rounded-[22px] overflow-hidden shadow-2xl shadow-black/40 border border-white/15 bg-white">
                    <img
                      src="https://customer-assets-4nw71qhi.emergentagent.net/job_70dcfe11-abf4-4d93-8774-5b062ca8667e/artifacts/yt35rxk9_StayMii%20x%20Starbucks.png"
                      alt="StayMii and Starbucks partnership — Better stay, better experience, better coffee. 15% off at Starbucks Malaysia for StayMii partner guests."
                      className="w-full h-auto"
                      loading="lazy"
                      width={1024}
                      height={1536}
                      decoding="async"
                      data-testid="starbucks-poster-img"
                    />
                  </div>
                  {/* floating badge */}
                  <div className="absolute -top-4 -right-3 sm:-right-4 bg-amber-300 text-[#0A3D2C] rounded-2xl shadow-xl px-4 py-3 rotate-3 hidden sm:flex flex-col items-center" data-testid="starbucks-float-badge">
                    <span className="text-2xl font-extrabold leading-none">15%</span>
                    <span className="text-[10px] font-bold uppercase tracking-wide">Off</span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
        )}

        {/* ═══════ 1c. LATEST NEWS / PRESS STRIP ═══════ */}
        <section className="relative py-20 lg:py-28 bg-[#0B1120] overflow-hidden" data-testid="home-news-strip">
          <div className="absolute inset-0 opacity-[0.4] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(96,165,250,0.14) 1px, transparent 0)', backgroundSize: '30px 30px' }} aria-hidden="true" />
          <div className="absolute -top-32 left-1/4 w-[32rem] h-[32rem] bg-blue-600/15 rounded-full blur-[150px] pointer-events-none" aria-hidden="true" />
          <div className="absolute -bottom-32 right-1/4 w-[28rem] h-[28rem] bg-indigo-600/10 rounded-full blur-[150px] pointer-events-none" aria-hidden="true" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-300 mb-4">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-400"></span>
                  </span>
                  In the news
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight">Momentum at StayMii&trade;</h2>
                <p className="text-sm text-slate-400 mt-3 max-w-xl">Partnerships, certifications and product milestones from Malaysia&rsquo;s hospitality and tourism technology platform.</p>
              </div>
              <Link to="/press" className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#0B1120] text-sm font-semibold hover:bg-blue-50 shadow-sm hover:shadow-lg transition-all group" data-testid="home-news-viewall">
                View all press <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {getLatestReleases(3).map((n, i) => {
                const tagClass = n.tag === 'Certification'
                  ? 'bg-emerald-400/15 text-emerald-300 border border-emerald-400/20'
                  : n.tag === 'Product Launch'
                  ? 'bg-indigo-400/15 text-indigo-300 border border-indigo-400/20'
                  : 'bg-blue-400/15 text-blue-300 border border-blue-400/20';
                return (
                  <Link
                    key={n.slug}
                    to={n.slug}
                    className="group relative flex flex-col rounded-3xl overflow-hidden bg-white/[0.04] border border-white/10 backdrop-blur-sm hover:bg-white/[0.07] hover:border-white/20 hover:-translate-y-1.5 transition-all duration-300 shadow-[0_1px_0_rgba(255,255,255,0.04)_inset]"
                    data-testid={`home-news-item-${i}`}
                  >
                    <div className={`relative aspect-[16/10] overflow-hidden ${n.imageBg || 'bg-[#0f1830]'}`}>
                      <img src={n.image} alt="" loading="lazy" className={`w-full h-full ${n.imageFit === 'contain' ? 'object-contain p-5' : 'object-cover'} group-hover:scale-105 transition-transform duration-500`} />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120]/50 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
                      {i === 0 && (
                        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 text-[#0B1120] text-[10px] font-bold uppercase tracking-wider shadow" data-testid="home-news-latest-badge">
                          <Sparkles className="w-3 h-3 text-blue-600" /> Latest
                        </span>
                      )}
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-3">
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {n.date}</span>
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${tagClass}`}>{n.tag}</span>
                      </div>
                      <h3 className="text-[15px] font-bold text-white leading-snug group-hover:text-blue-300 transition-colors line-clamp-3">{n.title}</h3>
                      <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-xs text-blue-400 font-semibold">Read the release <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>





        {/* ═══════ 1b. TRUST / "BUILT ON" CREDIBILITY STRIP ═══════ */}
        <section className="relative py-14 overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white" data-testid="trust-strip">
          <div className="absolute -top-16 left-1/4 w-80 h-80 bg-blue-300/20 rounded-full blur-[110px] pointer-events-none" aria-hidden="true" />
          <div className="absolute -bottom-16 right-1/4 w-80 h-80 bg-emerald-300/20 rounded-full blur-[110px] pointer-events-none" aria-hidden="true" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                <BadgeCheck className="h-4 w-4 text-emerald-500" /> Built on trusted, compliant infrastructure
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 lg:gap-5">
              {[
                { logo: lhdnLogo, label: 'LHDN MyInvois', sub: 'Compliant', border: 'border-rose-100', bar: 'from-rose-500 to-red-600', ring: 'ring-rose-100', text: 'text-rose-700', glow: 'hover:shadow-rose-200/60' },
                { logo: beds24Logo, label: 'Beds24', sub: 'PMS Partner', border: 'border-blue-100', bar: 'from-blue-500 to-indigo-600', ring: 'ring-blue-100', text: 'text-blue-700', glow: 'hover:shadow-blue-200/60' },
                { icon: ShieldCheck, label: 'PDPA', sub: 'Data Aligned', border: 'border-emerald-100', bar: 'from-emerald-500 to-teal-600', iconBg: 'from-emerald-500 to-teal-600', text: 'text-emerald-700', glow: 'hover:shadow-emerald-200/60' },
                { icon: Lock, label: 'SSL / TLS 1.3', sub: 'Encrypted', border: 'border-violet-100', bar: 'from-violet-500 to-purple-600', iconBg: 'from-violet-500 to-purple-600', text: 'text-violet-700', glow: 'hover:shadow-violet-200/60' },
                { icon: Cloud, label: 'Cloud Infra', sub: '99.9% Uptime', border: 'border-sky-100', bar: 'from-sky-500 to-cyan-600', iconBg: 'from-sky-500 to-cyan-600', text: 'text-sky-700', glow: 'hover:shadow-sky-200/60' },
              ].map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    className={`group relative flex flex-col items-center text-center gap-2.5 p-5 rounded-2xl bg-white border ${b.border} shadow-sm hover:shadow-2xl ${b.glow} hover:-translate-y-1.5 transition-all duration-300`}
                    data-testid={`trust-badge-${i}`}
                  >
                    <span className={`absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r ${b.bar}`} />
                    {b.logo ? (
                      <div className={`h-14 w-14 rounded-2xl flex items-center justify-center bg-white ring-2 ${b.ring} shadow-md group-hover:scale-110 transition-transform duration-300`}>
                        <img src={b.logo} alt={b.label} className="h-9 w-9 object-contain rounded" loading="lazy" />
                      </div>
                    ) : (
                      <div className={`h-14 w-14 rounded-2xl flex items-center justify-center bg-gradient-to-br ${b.iconBg} shadow-md group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="h-7 w-7 text-white" strokeWidth={2.2} />
                      </div>
                    )}
                    <span className={`text-[13px] font-extrabold ${b.text} leading-tight`}>{b.label}</span>
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{b.sub}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════ 2. LHDN E-INVOICE EXPLANATION ═══════ */}
        <section className="py-24 lg:py-32 bg-white relative overflow-hidden" data-testid="lhdn-section">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] bg-gradient-to-r from-red-100/40 via-transparent to-blue-100/40 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-6">
                <SectionTag><Scale className="h-4 w-4" /> LHDN Compliance</SectionTag>
                <h2 className="text-4xl lg:text-[44px] font-extrabold text-slate-900 mb-4 leading-tight tracking-tight" style={heading}>
                  LHDN e-Invoice Integration for Hotels & Businesses
                </h2>
                <p className="text-slate-500 text-base lg:text-lg leading-relaxed max-w-2xl mx-auto">
                  Malaysia's <Link to="/lhdn-e-invoice" className="text-[#2563EB] hover:underline font-medium">LHDN e-invoice mandate</Link> requires all businesses to submit digital invoices through the <Link to="/myinvois-api" className="text-[#2563EB] hover:underline font-medium">MyInvois API</Link>. StayMii automates the entire workflow — from data capture to LHDN submission.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-8 mt-14">
              <Reveal delay={60}>
                <div className="relative group bg-gradient-to-br from-red-50 via-rose-50 to-orange-50/50 rounded-[22px] p-9 lg:p-10 border border-red-100/80 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-200/30">
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-7 shadow-lg shadow-red-200/40 border border-red-100/60">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                  </div>
                  <p className="text-[11px] font-bold text-red-500 uppercase tracking-[0.15em] mb-5">Without e-Invoice Automation</p>
                  <p className="text-xl font-bold text-slate-900 mb-6" style={heading}>
                    Non-compliance risks fines up to RM200,000.
                  </p>
                  <div className="space-y-5">
                    {[
                      { text: 'Manual invoice creation across multiple systems', sub: 'Error-prone and time-consuming' },
                      { text: 'No built-in LHDN e-invoice support in your PMS', sub: "Your current system wasn't built for this" },
                      { text: 'Risk of non-compliance penalties from LHDN', sub: 'Fines are real and actively enforced' },
                      { text: 'Hours wasted on data entry and reconciliation', sub: 'Time your team could spend on guests' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3.5">
                        <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <XCircle className="w-4 h-4 text-red-500" />
                        </div>
                        <div>
                          <span className="text-slate-800 text-sm font-semibold block">{item.text}</span>
                          <span className="text-slate-400 text-xs">{item.sub}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>

              <Reveal delay={120}>
                <div className="relative group bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] rounded-[22px] p-9 lg:p-10 border border-blue-500/20 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/15">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/10 rounded-full blur-[60px] pointer-events-none" />
                  <div className="relative w-14 h-14 bg-[#2563EB] rounded-2xl flex items-center justify-center mb-7 shadow-lg shadow-blue-500/30">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <p className="relative text-[11px] font-bold text-blue-400 uppercase tracking-[0.15em] mb-5">With StayMii e-Invoice Plugin</p>
                  <p className="relative text-xl font-bold text-white mb-6" style={heading}>
                    Fully compliant, fully automated, zero stress.
                  </p>
                  <div className="relative space-y-5">
                    {[
                      { text: 'Auto-generate e-invoices from PMS or channel manager data', sub: 'Bookings become invoices instantly' },
                      { text: 'Direct submission to LHDN MyInvois API', sub: 'Compliant from the first transaction' },
                      { text: 'Real-time compliance monitoring & alerts', sub: 'Know your status before LHDN does' },
                      { text: 'Zero manual data entry — fully automated', sub: 'Works with hotels, Airbnb, and short-term rentals' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3.5">
                        <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle2 className="w-4 h-4 text-blue-400" />
                        </div>
                        <div>
                          <span className="text-white text-sm font-semibold block">{item.text}</span>
                          <span className="text-slate-400 text-xs">{item.sub}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="relative mt-8 pt-6 border-t border-white/10">
                    <Link to="/e-invoice-malaysia" className="group/btn inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-white transition-colors">
                      Learn more about e-Invoice Malaysia <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ═══════ 3. HOTEL / PMS USE CASE ═══════ */}
        <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="pms-section">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-50/80 via-white to-white pointer-events-none" />
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-200/20 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-16">
                <SectionTag><Hotel className="h-4 w-4" /> Hotel e-Invoice</SectionTag>
                <h2 className="text-4xl lg:text-[44px] font-extrabold text-slate-900 mb-5 leading-tight tracking-tight" style={heading}>
                  Automate Hotel Invoicing with MyInvois API
                </h2>
                <p className="text-slate-500 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
                  StayMii connects to your <Link to="/pms-e-invoice-integration" className="text-[#2563EB] hover:underline font-medium">hotel PMS</Link>, pulls booking and payment data, generates LHDN-compliant e-invoices, and submits them via the <Link to="/myinvois-api" className="text-[#2563EB] hover:underline font-medium">MyInvois API</Link> — all in real-time.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-3 gap-10 relative mb-20">
              <div className="hidden md:block absolute top-[72px] left-[33.3%] w-[10%] h-[2px] z-0">
                <div className="w-full h-full bg-gradient-to-r from-blue-300 to-blue-400 rounded-full" />
                <div className="travel-dot bg-blue-500" />
                <div className="travel-dot travel-dot-2 bg-blue-400" />
              </div>
              <div className="hidden md:block absolute top-[72px] left-[56.7%] w-[10%] h-[2px] z-0">
                <div className="w-full h-full bg-gradient-to-r from-blue-400 to-emerald-400 rounded-full" />
                <div className="travel-dot bg-emerald-500" />
                <div className="travel-dot travel-dot-2 bg-emerald-400" />
              </div>

              {[
                { step: '01', title: 'Connect Your PMS or System', desc: 'Integrate your hotel PMS, channel manager, or start with StayMii Lite. We handle the setup — no technical work needed.', trust: 'Go live in days, not months', icon: Plug, gradient: 'from-blue-500 to-blue-700', shadow: 'shadow-blue-500/20' },
                { step: '02', title: 'e-Invoices Generated Automatically', desc: 'Every hotel booking, OTA reservation, and guest payment is converted into a validated e-invoice in real time.', trust: 'No manual entry. No errors.', icon: Receipt, gradient: 'from-[#0F172A] to-[#1E293B]', shadow: 'shadow-slate-500/20' },
                { step: '03', title: 'Submit to LHDN Seamlessly', desc: 'StayMii submits directly to LHDN MyInvois and tracks compliance status in real-time for every invoice.', trust: 'Always compliant. Always up to date.', icon: Shield, gradient: 'from-emerald-500 to-emerald-700', shadow: 'shadow-emerald-500/20' },
              ].map((item, i) => (
                <Reveal key={i} delay={i * 100}>
                  <div className="step-card relative bg-white/80 backdrop-blur-sm rounded-[22px] p-8 border border-slate-200/80 text-center hover:-translate-y-2 hover:shadow-xl transition-all duration-300 z-10 h-full flex flex-col" data-testid={`step-${item.step}`}>
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-white border border-slate-200 rounded-full text-[11px] font-bold text-[#2563EB] uppercase tracking-wider">Step {item.step}</span>
                    <div className={`w-16 h-16 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 mt-3 shadow-lg ${item.shadow}`}>
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-3" style={heading}>{item.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-5 flex-1">{item.desc}</p>
                    <p className="text-[11px] text-emerald-600 font-semibold border-t border-slate-100 pt-4 mt-auto">
                      <CheckCircle2 className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />{item.trust}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={200}>
              <div className="text-center">
                <Link to="/hotel-e-invoice-system" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors">
                  Learn more about hotel e-invoice systems <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ 4. BUILT FOR PMS, CHANNEL MANAGERS & AIRBNB ═══════ */}
        <section className="py-20 lg:py-24 bg-white" data-testid="integration-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-14">
                <SectionTag><Plug className="h-4 w-4" /> Integrations</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={heading}>Built for PMS, Channel Managers & Airbnb Operators</h2>
                <p className="text-slate-600 text-lg">
                  StayMii integrates with your existing system — whether it's a hotel PMS, channel manager, or booking platform. No migration, no disruption.
                </p>
              </div>
            </Reveal>

            <Reveal delay={80}>
              <div className="flex flex-wrap justify-center gap-6 lg:gap-8 mb-12">
                {partners.map((p, i) => (
                  <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
                    className="relative flex flex-col items-center gap-3 px-7 py-6 bg-white border border-slate-200 rounded-xl hover:shadow-lg hover:border-slate-300 hover:-translate-y-1 transition-all min-w-[140px]"
                    data-testid={`partner-logo-${p.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {p.logo ? (
                      <img src={p.logo} alt={`${p.name} PMS e-invoice integration`} className="h-10 w-auto object-contain rounded" loading="lazy" />
                    ) : (
                      <div className="h-10 flex items-center justify-center">
                        <span className="text-xl font-bold text-slate-800">{p.name}</span>
                      </div>
                    )}
                    <span className="text-xs font-semibold text-slate-600">{p.name}</span>
                    {p.tag && (
                      <span className="text-[10px] font-bold text-[#2563EB] bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full leading-tight text-center">{p.tag}</span>
                    )}
                  </a>
                ))}
              </div>
            </Reveal>

            {/* Internal links grid */}
            <Reveal delay={120}>
              <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <Link to="/pms-e-invoice-integration" className="group p-4 rounded-xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50/50 transition-all text-center">
                  <Laptop className="w-6 h-6 text-[#2563EB] mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-800 group-hover:text-[#2563EB]">PMS e-Invoice Integration</p>
                  <p className="text-xs text-slate-400 mt-1">Connect your hotel PMS</p>
                </Link>
                <Link to="/channel-manager-e-invoice" className="group p-4 rounded-xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50/50 transition-all text-center">
                  <Globe className="w-6 h-6 text-[#2563EB] mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-800 group-hover:text-[#2563EB]">Channel Manager e-Invoice</p>
                  <p className="text-xs text-slate-400 mt-1">Sync OTA bookings</p>
                </Link>
                <Link to="/airbnb-e-invoice-malaysia" className="group p-4 rounded-xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50/50 transition-all text-center">
                  <HomeIcon className="w-6 h-6 text-[#2563EB] mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-800 group-hover:text-[#2563EB]">Airbnb e-Invoice Malaysia</p>
                  <p className="text-xs text-slate-400 mt-1">For short-term rental hosts</p>
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ 5. CHANNEL MANAGER + AIRBNB / SHORT-TERM RENTAL ═══════ */}
        <section className="py-20 lg:py-24 bg-slate-50" data-testid="use-case-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-14">
                <SectionTag><Wifi className="h-4 w-4" /> Use Cases</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={heading}>e-Invoice for Short-Term Rental & Hospitality Systems</h2>
                <p className="text-slate-600 text-lg">
                  Whether you manage hotel bookings via a channel manager or run Airbnb properties, StayMii handles your <Link to="/e-invoice-malaysia" className="text-[#2563EB] hover:underline font-medium">e-invoice compliance</Link> automatically.
                </p>
              </div>
            </Reveal>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {[
                { icon: Hotel, title: 'Hotels & Resorts', desc: 'Automate guest billing e-invoices from PMS check-in/check-out data. Multi-property support included.', link: '/hotel-e-invoice-system', linkText: 'Hotel e-Invoice System' },
                { icon: Globe, title: 'Channel Manager Operators', desc: 'Sync bookings from Booking.com, Agoda, Expedia via your channel manager. Auto-generate e-invoices for every OTA reservation.', link: '/channel-manager-e-invoice', linkText: 'Channel Manager Integration' },
                { icon: HomeIcon, title: 'Airbnb & Short-Term Rentals', desc: 'Multiple property invoicing, automated OTA data pull, and LHDN compliance for Airbnb hosts and short-stay operators.', link: '/airbnb-e-invoice-malaysia', linkText: 'Airbnb e-Invoice Guide' },
                { icon: Building2, title: 'Service Apartments', desc: 'Long-stay invoicing with recurring billing automation. Connect your property management system seamlessly.', link: '/pms-e-invoice-integration', linkText: 'PMS Integration' },
                { icon: Users, title: 'Boutique Hotel Groups', desc: 'Centralised e-invoice dashboard across multiple properties. One compliance view for your entire portfolio.', link: '/hotel-e-invoice-system', linkText: 'Hotel e-Invoice' },
              ].map((card, i) => (
                <Reveal key={i} delay={i * 60}>
                  <div className="bg-white rounded-2xl p-7 border border-slate-200 hover:shadow-lg hover:border-slate-300 transition-all h-full group flex flex-col" data-testid={`use-case-card-${i}`}>
                    <div className="w-12 h-12 bg-[#2563EB] rounded-xl flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
                      <card.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2">{card.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">{card.desc}</p>
                    <Link to={card.link} className="text-xs font-semibold text-[#2563EB] hover:text-[#1D4ED8] inline-flex items-center gap-1">
                      {card.linkText} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ 6. SIMPLE INTEGRATION ═══════ */}
        <section className="py-20 lg:py-24 bg-white" data-testid="simple-integration-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-14">
                <SectionTag><Settings className="h-4 w-4" /> Simple Setup</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={heading}>Simple Integration with Any Booking or Management System</h2>
                <p className="text-slate-600 text-lg">
                  Everything you need for <Link to="/lhdn-e-invoice" className="text-[#2563EB] hover:underline font-medium">LHDN e-invoice compliance</Link> — automated and integrated with your existing workflow.
                </p>
              </div>
            </Reveal>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Receipt, title: 'LHDN-Compliant E-Invoicing', desc: 'Generate e-invoices that meet all LHDN MyInvois requirements. Every invoice is validated before submission.', color: 'bg-[#2563EB]' },
                { icon: Zap, title: 'Automated Invoice Generation', desc: 'Invoices auto-created from your PMS, channel manager, or booking data. Zero manual entry required.', color: 'bg-[#0F172A]' },
                { icon: Scale, title: 'MyInvois API Submission', desc: 'Direct submission to LHDN via the MyInvois API with error handling, retry logic, and full audit trail.', color: 'bg-purple-600' },
                { icon: BarChart3, title: 'Financial Reporting', desc: 'Real-time analytics on submission status, revenue tracking, and exportable compliance reports.', color: 'bg-emerald-600' },
              ].map((card, i) => (
                <Reveal key={i} delay={i * 60}>
                  <div className="bg-white rounded-2xl p-7 border border-slate-200 hover:shadow-lg hover:border-slate-300 transition-all h-full group" data-testid={`feature-card-${i}`}>
                    <div className={`w-12 h-12 ${card.color} rounded-xl flex items-center justify-center mb-5 group-hover:scale-105 transition-transform`}>
                      <card.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2">{card.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{card.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={300}>
              <div className="mt-14 max-w-4xl mx-auto">
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl shadow-slate-200/50">
                  <img
                    src="https://customer-assets.emergentagent.com/job_5e74271a-8fbb-4f1f-8b01-d4ef0a762567/artifacts/3guzdbw8_staymii_demo_clean.gif"
                    alt="StayMii e-invoice dashboard — real-time LHDN submission, hotel compliance status, and revenue tracking"
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
                <p className="text-center text-xs text-slate-400 mt-4 font-medium">Live dashboard — e-invoices, LHDN compliance status, and revenue at a glance</p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ 7. WHO IT'S FOR ═══════ */}
        <section className="py-20 lg:py-24 bg-white" data-testid="trust-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-14">
                <SectionTag><Building2 className="h-4 w-4" /> Who It's For</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4" style={heading}>Built for Malaysian Hospitality Operators</h2>
                <p className="text-slate-600 text-lg">
                  Whether you run a hotel chain, service apartment, or a single Airbnb property — StayMii ensures your e-invoicing is LHDN-compliant.
                </p>
              </div>
            </Reveal>

            <Reveal delay={60}>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {[
                  { img: 'https://customer-assets.emergentagent.com/job_5e74271a-8fbb-4f1f-8b01-d4ef0a762567/artifacts/cerpii62_d_oYalHt_QNEKw2LgBe22PbMkRthlqK5w0cBvWyGgZaYXWhD_Xtt-vmqzld72_xROoCSchs2Cxl_RzcCwlOTVfU4_PS0z37XIa2btAqxujwP9Lnm_ihfY76Cn5Di1DFDimA81E8kLOX8db55oNDPqUJxct9QttHoTlEjeg4lIwtPEJtQtlMbmrG1Zd-l_9U4.jpg', label: 'Hotels & Resorts', desc: 'Multi-property e-invoice compliance' },
                  { img: 'https://customer-assets.emergentagent.com/job_5e74271a-8fbb-4f1f-8b01-d4ef0a762567/artifacts/rqz0ypwb_06_sqIjugrE7kXRv7s7s-bHHIXP3ABepVsbmu_0P-qjiEyge-1pNrjtzZavmlcvab7DIpoTmIfmiKz1q_WwSXzR4dmiII5Whl7QAUBV9WkYzUZ7GhrPPtHES7RjHtLpW1AaTuK0eqepwbhc_Z-o6zhMT5QhBldnvVJ8bIXrUAAqjwy_gjA7bDVFUyaRcqfRN.jpg', label: 'Service Apartments', desc: 'Automated invoicing for long-stay' },
                  { img: 'https://customer-assets.emergentagent.com/job_5e74271a-8fbb-4f1f-8b01-d4ef0a762567/artifacts/2umluikx__eLIZEIfK5woqi9VHH4jf1SJaOLeU3x3GoorBd38q3C6icWAkHcLRsudz7_QbXv_Lgsu1UjbZQPSGtEjNNM-ZrqcWCR2j7XGxDxvVhPdfWANKi_IgU-m6OxsxeGTIU6RjgbSMTTs_EyBrAaFtU0WiNW1TXHeC5gZZH1o-CPJh9zpFzb7ic6RSy6S21pBsjsu.jpg', label: 'Airbnb & Short-Term Rentals', desc: 'OTA booking e-invoice automation' },
                  { img: 'https://customer-assets.emergentagent.com/job_5e74271a-8fbb-4f1f-8b01-d4ef0a762567/artifacts/ip1p1r5h_tnysGhxdpRGmcyrOmxC4eqmXLKmqGTBkQqX8FvaQSEXKyPUXkYREChXGqCNo1JEEQUwakdFC6AgW-PAuCAzYkrrK5-Gb-COubK3v9wEJcz0x5VBGGoWKSzk8wfbEkspVZDw37u-1wgjgYHrqyx6UnpItEcVsRMaqn3bwWItW5CCwgspfZPQBjFjoWNRN4JE0.jpg', label: 'Boutique Groups', desc: 'Centralised reporting across properties' },
                ].map((item, i) => (
                  <div key={i} className="group relative rounded-2xl overflow-hidden aspect-[3/4] cursor-default" data-testid={`operator-card-${i}`}>
                    <img src={item.img} alt={`${item.label} — e-invoice Malaysia`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <p className="text-base font-bold text-white mb-1">{item.label}</p>
                      <p className="text-xs text-white/70">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  { metric: 'RM59', label: 'Starting price per month' },
                  { metric: '< 1 week', label: 'Average setup time' },
                  { metric: '100%', label: 'LHDN MyInvois compliance' },
                ].map((stat, i) => (
                  <div key={i} className="text-center py-6 px-4 bg-[#0F172A] rounded-xl">
                    <p className="text-2xl lg:text-3xl font-bold text-white mb-1">{stat.metric}</p>
                    <p className="text-xs text-white/50 font-medium">{stat.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ 8. StayMii Lite mention ═══════ */}
        <div className="py-5 bg-white text-center" data-testid="lite-mention">
          <p className="text-slate-400 text-xs font-medium">
            No PMS? <Link to="/get-started" className="text-slate-500 underline underline-offset-2 hover:text-slate-700 transition-colors">Start with StayMii Lite</Link>.
          </p>
        </div>

        {/* ═══════ 9. FAQ ═══════ */}
        <FaqSection />

        {/* ═══════ 10. FINAL CTA ═══════ */}
        <section className="py-20 lg:py-28 bg-[#0B1120]" data-testid="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6" data-testid="cta-headline">
                Get Compliant Without Changing Your System
              </h2>
              <p className="text-lg text-white/65 mb-10 max-w-2xl mx-auto leading-relaxed">
                Join hotels, Airbnb hosts, and short-term rental operators across Malaysia using StayMii to automate <Link to="/e-invoice-malaysia" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">e-invoicing</Link> and stay <Link to="/lhdn-e-invoice" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">LHDN-compliant</Link>.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/einvoice/portal/auth" className="group px-8 py-4 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:shadow-blue-500/20 transition-all flex items-center justify-center gap-2" data-testid="cta-primary-btn">
                  Get Started <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/contact" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold text-lg hover:bg-white/15 transition-all border border-white/20 flex items-center justify-center gap-2" data-testid="cta-secondary-btn">
                  Book Consultation
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <HRPromoCard />

        <LandingFooter />
      </div>
    </>
  );
};

export default Home;
